import { Injectable } from '@angular/core';
import { Question } from '@app/forms/subscription/definition/interface';
import { PledgeRead } from '@app/forms/subscription/definition/interface/Pledge';
import { ValidationBridgeService } from './validation-bridge.service';

import { AuthentificationService } from '@app/authentification/authentification.service';
import { UserAuthentified } from '@app/authentification/user-authentified.interface';
import { isSaleViaAlbR } from '@app/forms/subscription/definition/constants/organization.constants';
import {
  CompanyFieldType,
  SubscriberTypeEnum,
} from '@app/forms/subscription/definition/interface/enum';
import * as _ from 'lodash';
import {
  isPonctuelProgrammed,
  isRppOperation,
  RACHAT_PARTIEL_OPERATION,
  ratiosAreComplete,
  RPP_OPERATION,
} from '../../ea-subscription-forms/operations-form/rachat/services/rachat-allocation';
import { findAdditionalQuestions } from './utile';

const PER_ALBR_PRODUCT_ID = 6031302510;

@Injectable({
  providedIn: 'root',
})
export class FinancialKnowledgeQustionsService {
  rcdaDevisSelectionVisited = false;

  constructor(
    private cBService: ValidationBridgeService,
    private authService: AuthentificationService,
  ) {}

  // TEMPORAIRE (PRE-4906) : les 4 slides "Taux par support" (souscription, arbitrage, versement,
  // transfert) sont volontairement masquées à tous les consultants tant que la feature n'est pas
  // jugée complète, sauf pour les personnes ayant le rôle "R_IT" (tests en conditions réelles avant
  // la bascule en prod). À SUPPRIMER (et retirer les 8 usages ci-dessous dans callbackBasedOn/
  // alwaysShow) une fois le go donné pour la mise en production définitive.
  private isTauxParSupportFeatureVisible(): boolean {
    return this.authService.hasRole('R_IT');
  }

  financialKnowledgeQustions: Question[] = [
    {
      name: 'financialMarket',
      template: 'financialMarket',
      callbackValidation: entity => {
        return true;
      },
      title: 'Questions supplémentaires ( Marchés financiers )',
      type: 'owners',
      category: 'Connaissances financières',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'additionalQuestionInvestment',
      template: 'additionalQuestionInvestment',
      callbackValidation: entity => {
        return true;
      },
      title: 'Questions supplémentaires ( Investissements )',
      type: 'owners',
      category: 'Connaissances financières',
      productType: ['SCPI', 'CROWDFUNDING'],
      // productType: [
      //   ...excludeProductTypes(["LMNP", "SOFICA", "TANGIBLES", "DEFISCALISATION"])
      //  ],
    },
    {
      name: 'realEstateMarkets',
      template: 'realEstateMarkets',
      callbackValidation: entity => {
        return true;
      },
      title: 'Questions supplémentaires ( Marchés immobiliers )',
      type: 'owners',
      category: 'Connaissances financières',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'riskProfilFirstPartForm',
      template: 'riskProfilFirstPartForm',
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { maritalStatus, occupancyStatus } = riskProfil || {};

          return !!maritalStatus && !!occupancyStatus;
        });

        return allOwnersValid;
      },
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasCompany = owners?.some(({ physicalUser }) => !physicalUser);
        return hasCompany;
      },
      title: 'Questions profil de risque 1/5',
      type: 'owners',
      category: 'Connaissances financières',

      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilCompanyFirstPartForm',
      template: 'riskProfilCompanyFirstPartForm',
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { legalStructure, patrimonyValue } = riskProfil || {};

          return !!legalStructure && !!patrimonyValue;
        });

        return allOwnersValid;
      },
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasPhysical = owners?.some(({ physicalUser }) => !!physicalUser);
        return hasPhysical;
      },
      title: 'Questions profil de risque 1/5',
      type: 'owners',
      category: 'Connaissances financières',

      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilSecondPartForm',
      template: 'riskProfilSecondPartForm',
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasCompany = owners?.some(({ physicalUser }) => !physicalUser);
        return hasCompany;
      },
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { workInFinance, readSpecializedPress, credit } =
            riskProfil || {};

          return !!workInFinance && !!readSpecializedPress && !!credit;
        });

        return allOwnersValid;
      },
      title: 'Questions profil de risque 2/5',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilCompanySecondPartForm',
      template: 'riskProfilCompanySecondPartForm',
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasPhysical = owners?.some(({ physicalUser }) => !!physicalUser);
        return hasPhysical;
      },
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { resultatNet, leasing, tresorerieAPlacer } = riskProfil || {};

          return !!resultatNet && !!leasing && !!tresorerieAPlacer;
        });

        return allOwnersValid;
      },
      title: 'Questions profil de risque 2/5',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilThirdPartForm',
      template: 'riskProfilThirdPartForm',
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getRiskTolerance(entity);
      },
      title: 'Questions profil de risque 3/5',
      subTitle:
        'En vous basant sur le schéma ci-dessous, choisissez le placement qui vous correspond le mieux.',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilFourthPartForm',
      template: 'riskProfilFourthPartForm',
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasCompany = owners?.some(({ physicalUser }) => !physicalUser);
        return hasCompany;
      },
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { income, patrimonyValue } = riskProfil || {};

          return !!income && !!patrimonyValue;
        });

        return allOwnersValid;
      },
      title: 'Questions profil de risque 4/5',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilCompanyFourthPartForm',
      template: 'riskProfilCompanyFourthPartForm',
      callbackBasedOn: (entity: PledgeRead) => {
        const { owners = [] } = entity || {};
        const hasPhysical = owners?.some(({ physicalUser }) => !!physicalUser);
        return hasPhysical;
      },
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { caValue, dateDeCreation } = riskProfil || {};

          return !!caValue && !!dateDeCreation;
        });

        return allOwnersValid;
      },
      title: 'Questions profil de risque 4/5',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'riskProfilFifthPartForm',
      template: 'riskProfilFifthPartForm',
      callbackValidation: (entity: PledgeRead) => {
        const owners = entity?.owners || [];

        const allOwnersValid = owners.every(owner => {
          const { riskProfil } = owner;
          const { desiredYield, conceptReturnRisk, lossTolerance } =
            riskProfil || {};

          return !!desiredYield && !!conceptReturnRisk && !!lossTolerance;
        });

        return allOwnersValid;
      },
      title: 'Questions profil de risque 5/5',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'existingInvestments',
      template: 'existingInvestments',
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getExistingInvestments(entity);
      },
      title: "Avez-vous connu une baisse de l'un de vos placements ?",
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'savingsRate',
      template: 'savingsRate',
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getSavingsRate(entity);
      },
      title:
        "Vous avez 100€ sur un compte d'épargne rémunéré à 2% par an. Combien aurez-vous dans 5 ans ?",
      type: 'owners',
      category: 'Connaissances financières',
      categoryId: 3,
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },

    {
      name: 'moneyGames',
      template: 'moneyGames',
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getMoneyGames(entity);
      },
      title:
        'Jouez vous à des jeux d’argent ( jeux en ligne, paris sportifs, casino ... ) ?',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'investmentKnowledge',
      template: 'investmentKnowledge',
      callbackValidation: (entity: PledgeRead) => {
        // const result = this.cBService.getInvestmentKnowledge(entity);

        // return result;
        return true;
      },
      title: 'Quels placements connaissez-vous ?',
      subTitle:
        'Sélectionnez dans la liste les placements que vous connaissez ou que vous possédez.',
      type: 'owners',
      category: 'Connaissances financières',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'financialKnowledge1',
      template: 'financialKnowledge1',
      title: 'Évaluons vos connaissances produits',
      subTitle:
        "Cela nous permettra de vous faire des recommandations adaptées à votre profil d'investisseur.",
      type: 'owners',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackConsultant: pledge => {
        const p1 = [
          'knowMonetaryProduct',
          'knowBond',
          'knowStock',
          'knowPrivateEquity',
          'knowLifeInsurance',
          'knowDerivativeProducts',
          'knowScpi',
        ];

        const previousSlideValid = p1?.some(p =>
          pledge?.owners?.some(o => o?.riskProfil?.[p]),
        );
        return previousSlideValid;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const p1 = [
          'knowMonetaryProduct',
          'knowBond',
          'knowStock',
          'knowPrivateEquity',
          'knowLifeInsurance',
          'knowDerivativeProducts',
          'knowScpi',
        ];

        return !p1?.some(p => pledge?.owners?.some(o => o?.riskProfil?.[p]));
      },
      category: 'Connaissances financières',
      categoryId: 9,
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
      ],
    },
    {
      name: 'initializationCredit',
      title: 'Composez votre crédit',
      template: 'initializationCredit',
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditGeneralInformations',
      title: 'Informations générales',
      template: 'creditGeneralInformations',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditGeneralInformations(pledge);
      },
      alwaysShow(entity: any, user) {
        return true;
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditInformations',
      title: 'Informations du crédit',
      template: 'creditInformations',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditInformations(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditDiffere',
      title: 'Informations sur le différé',
      template: 'creditDiffere',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditDiffere(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditInsuranceQuotite',
      title: 'Informations sur la quotité',
      template: 'creditInsuranceQuotite',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditInsuranceQuotite(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditGuarantees',
      title: 'Informations sur les garanties',
      template: 'creditGuarantees',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditGuarantees(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditOptionTarification',
      title: 'Options de tarification',
      template: 'creditOptionTarification',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditOptionTarification(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return user?.sessionConsultant ? false : true;
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditOwnersProfessionDetails',
      title: 'Informations supplémentaires (1/2)',
      template: 'creditOwnersProfessionDetails',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getOwnersProfessionDetails(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return user?.sessionConsultant ? false : true;
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditOutstandings',
      title: 'Informations supplémentaires (2/2)',
      template: 'creditOutstandings',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditOutstandings(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditManualTarification',
      title: 'Configuration tarification',
      template: 'creditManualTarification',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditManualTarification(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        const product = pledge?.products?.[0];
        if (product?.parameters?.borrowerInsuranceMode === 'API') {
          return true;
        }
        return false;
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditTarification',
      title: 'Tarification',
      template: 'creditTarification',
      callbackBasedOn: (pledge: PledgeRead) => {
        const product = pledge?.products?.[0];
        if (product?.parameters?.borrowerInsuranceMode === 'MANUAL') {
          return true;
        }
        return pledge.products?.[0]?.parameters?.tarifications?.length === 0;
      },
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditTarification(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditAdditionnalInformations',
      title: 'Informations additionnelles',
      template: 'creditAdditionnalInformations',
      callbackBasedOn: (pledge: PledgeRead) => {
        const product = pledge?.products?.[0];
        if (product?.parameters?.borrowerInsuranceMode === 'MANUAL') {
          return true;
        }
        return _.isNil(pledge.products?.[0]?.parameters?.choosedProductCode);
      },
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCreditAdditionnalInformations(pledge);
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'creditDocumentationAssureur',
      title: "Documentation de l'assureur",
      template: 'creditDocumentationAssureur',
      callbackBasedOn: (pledge: PledgeRead) => {
        const product = pledge?.products?.[0];
        if (product?.parameters?.borrowerInsuranceMode === 'MANUAL') {
          return true;
        }
        return pledge.products?.[0]?.parameters?.insurerDocuments?.length === 0;
      },
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      category: 'Composition du crédit',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'initializationContract',
      title: 'Composez votre contrat',
      template: 'initializationContract',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
      ],
    },
    {
      name: 'contractInformations',
      title: 'Informations sur votre contrat',
      template: 'contractInformations',
      callbackBasedOn(entity: any) {
        return ![
          'ARBITRAGE_INDIVIDUAL',
          'RACHAT_PARTIEL',
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
          'RACHAT_TOTAL',
          'RACHAT_PARTIEL_PROGRAMME',
          'SUSPENSION_INDEXATION',
          'AVANCE',
          'TRANSFER',
          'ORDRE_DE_REMPLACEMENT',
          'SUSPENSION_INDEXATION',
        ].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getContractInformations(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'transferForm',
      title: "Informations complémentaires sur l'opération",
      template: 'transferForm',
      callbackBasedOn(entity: any) {
        return !['TRANSFER', 'ORDRE_DE_REMPLACEMENT'].includes(
          entity?.operation,
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getTransferInfo(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatPartielTypeForm',
      title: 'Type de rachat partiel',
      subTitle:
        'Souhaitez-vous compléter votre rachat ponctuel par des rachats programmés ?',
      template: 'rachatPartielTypeForm',
      callbackBasedOn: (entity: any) => {
        return (
          entity?.operation !== RACHAT_PARTIEL_OPERATION ||
          ['COMPTE_TITRE', 'PEA'].includes(entity?.productType)
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getRachatPartielTypeForm(entity);
      },
      alwaysShow: (entity: any, user) => {
        return (
          entity?.operation === RACHAT_PARTIEL_OPERATION &&
          !['COMPTE_TITRE', 'PEA'].includes(entity?.productType)
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatForm',
      title: "Informations complémentaires sur l'opération",
      template: 'rachatForm',
      callbackBasedOn(entity: any) {
        return ![
          'RACHAT_TOTAL',
          'RACHAT_PARTIEL',
          'AVANCE',
          'RACHAT_PARTIEL_PROGRAMME',
        ].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getRachatInfo(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'transferSupportsForm',
      title: 'Répartition du transfert vers le nouveau contrat',
      template: 'transferSupportsForm',
      callbackBasedOn(entity: any) {
        return !['TRANSFER'].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation = this.cBService.getTransferSupportsForm(entity);
        const { products = [] } = entity || {};

        const hasInvestment = !!products?.[0]?.parameters
          ?.currentSupportRepartition?.supportRepartition?.length
          ? products[0].parameters?.currentSupportRepartition?.supportRepartition?.map(
              ({ amount }) => amount,
            ) || []
          : [];

        return _.sum(hasInvestment) > 0 && formValidation;
      },
      alwaysShow(entity, user) {
        return ['TRANSFER'].includes(entity?.operation);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      // PRE-4896/PRE-4906 : slide "Taux par support" (transfert) — visible uniquement pour un
      // consultant, quelle que soit l'organisation. Éditable pour les ventes en direct ; affichée
      // en lecture seule (champs désactivés, cf. taux-par-support-transfer.component.ts et
      // isSaleViaAlbR()) pour toute vente via AlbR (organisation affiliée EUODIA, ou produit sans
      // vente en direct). Absente pour ORDRE_DE_REMPLACEMENT (hors périmètre).
      name: 'tauxParSupportTransfer',
      getTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? 'Vérifiez les commissions par support'
          : 'Saisissez les commissions par support';
      },
      getSubTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? "Vérifiez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, vérifiez l'upfront. En cas d'erreur, contactez les équipes dédiées."
          : "Renseignez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, saisissez uniquement l'upfront.";
      },
      template: 'tauxParSupportTransfer',
      isConsultant: true,
      // TEMPORAIRE (PRE-4906) : masquée sauf rôle R_IT tant que la feature n'est pas complète —
      // cf. isTauxParSupportFeatureVisible(), à retirer avant la mise en prod définitive.
      callbackBasedOn: (entity: any) => {
        return (
          !['TRANSFER'].includes(entity?.operation) ||
          !this.isTauxParSupportFeatureVisible()
        );
      },
      alwaysShow: (entity, user) => {
        return (
          ['TRANSFER'].includes(entity?.operation) &&
          this.isTauxParSupportFeatureVisible()
        );
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'ordreDeRemplacementForm',
      title: 'Répartition actuelle',
      template: 'ordreDeRemplacementForm',
      callbackBasedOn(entity: any) {
        return !['ORDRE_DE_REMPLACEMENT'].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation = this.cBService.getTransferSupportsForm(entity);
        const { products = [] } = entity || {};

        const hasInvestment = !!products?.[0]?.parameters
          ?.currentSupportRepartition?.supportRepartition?.length
          ? products[0].parameters?.currentSupportRepartition?.supportRepartition?.map(
              ({ amount }) => amount,
            ) || []
          : [];

        return _.sum(hasInvestment) > 0 && formValidation;
      },
      alwaysShow(entity, user) {
        return ['ORDRE_DE_REMPLACEMENT'].includes(entity?.operation);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'arbitrageType',
      title: "Type d'arbitrage",
      template: 'arbitrageType',
      callbackBasedOn(entity: any) {
        return entity?.operation !== 'ARBITRAGE_INDIVIDUAL';
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getArbitrageType(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'arbitrageReason',
      title: "Motif de l'opération",
      template: 'arbitrageReason',
      callbackBasedOn(entity: any) {
        return ![
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
          'ARBITRAGE_INDIVIDUAL',
          'RACHAT_PARTIEL',
          'RACHAT_PARTIEL_PROGRAMME',
        ].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getArbitrageReason(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'operationCurrentAllocationForm',
      title: 'Répartition actuelle',
      template: 'operationCurrentAllocationForm',
      callbackBasedOn(entity: any) {
        return ![
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
          'RACHAT_TOTAL',
          'RACHAT_PARTIEL',
          'AVANCE',
          'RACHAT_PARTIEL_PROGRAMME',
          'SUSPENSION_INDEXATION',
        ].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return true;
      },
      alwaysShow(entity, user) {
        return [
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
          'RACHAT_TOTAL',
          'RACHAT_PARTIEL',
          'RACHAT_PARTIEL_PROGRAMME',
          'SUSPENSION_INDEXATION',
          'AVANCE',
        ].includes(entity?.operation);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'operationProgrammedPaymentsFrequencyForm',
      title: 'Fréquence des versements programmés',
      template: 'operationProgrammedPaymentsFrequencyForm',
      callbackBasedOn(entity: any) {
        return !['VERSEMENT_PROGRAMME'].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const { products = [] } = entity || {};
        return products?.every(
          ({ parameters }) => !!parameters?.avProgammedPaymentType,
        );
      },
      alwaysShow(entity, user) {
        return ['VERSEMENT_PROGRAMME'].includes(entity?.operation);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'versementAmountForm',
      title: 'Montant du versement',
      getTitle(entity: PledgeRead) {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return 'VERSEMENT_PROGRAMME' === operation
          ? 'Montant du versement programmé'
          : 'Montant du versement';
      },
      template: 'versementAmountForm',
      callbackBasedOn(entity: any) {
        return !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
          entity?.operation,
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getVersementAmountForm(entity);
      },
      alwaysShow(entity, user) {
        return ['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
          entity?.operation,
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'versementInvestmentAllocation',
      title: 'Comment souhaitez-vous répartir votre versement ?',
      getTitle(entity: PledgeRead) {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return 'VERSEMENT_PROGRAMME' === operation
          ? 'Comment souhaitez-vous répartir votre versement programmé ?'
          : 'Comment souhaitez-vous répartir votre versement ?';
      },
      subTitle:
        'Différentes formules sont proposées pour composer la nouvelle allocation de votre contrat.',
      template: 'versementInvestmentAllocation',
      type: 'products',
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return true;
        }

        return products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const { products = [] } = entity || {};
        return products?.every(
          ({ parameters }) => !!parameters?.versementDistribution,
        );
      },
      alwaysShow(entity, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return false;
        }

        return !products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'versementSimulationPacks',
      title: 'Choisissez votre allocation type.',
      template: 'versementSimulationPacks',
      type: 'products',
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return true;
        }

        if (
          products?.some(
            product => product?.parameters?.partnerFlux === 'CORUM',
          )
        ) {
          return true;
        }

        return !products?.every(
          ({ parameters }) => parameters?.versementDistribution === 'PACK',
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const { products = [] } = entity || {};
        return products?.every(({ parameters }) => !!parameters?.productPackId);
      },
      alwaysShow(entity, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return false;
        }

        if (
          products?.some(
            product => product?.parameters?.partnerFlux === 'CORUM',
          )
        ) {
          return false;
        }

        return products?.every(
          ({ parameters }) => parameters?.versementDistribution === 'PACK',
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'versementsLibresSupportsForm',
      title: 'Versements libres',
      template: 'versementsLibresSupportsForm',
      getTitle(entity: PledgeRead) {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return 'VERSEMENT_PROGRAMME' === operation
          ? 'Versements programmés'
          : 'Versements libres';
      },
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return true;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return (
          this.cBService.getVersementLibresSupportsAllocation(entity) &&
          this.cBService.getVersementLibresSupportsForm(entity)
        );
      },
      alwaysShow(entity, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return false;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      // PRE-4896/PRE-4906 : slide "Taux par support" (versement complémentaire / programmé) —
      // visible uniquement pour un consultant, quelle que soit l'organisation. Éditable pour les
      // ventes en direct ; affichée en lecture seule (champs désactivés, cf.
      // taux-par-support-versement.component.ts et isSaleViaAlbR()) pour toute vente via AlbR
      // (organisation affiliée EUODIA, ou produit sans vente en direct). Même exclusion
      // partenaire CORUM que "versementsLibresSupportsForm" (pas de liste de supports saisie dans
      // ce cas, donc rien à tarifer).
      name: 'tauxParSupportVersement',
      getTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? 'Vérifiez les commissions par support'
          : 'Saisissez les commissions par support';
      },
      getSubTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? "Vérifiez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, vérifiez l'upfront. En cas d'erreur, contactez les équipes dédiées."
          : "Renseignez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, saisissez uniquement l'upfront.";
      },
      template: 'tauxParSupportVersement',
      isConsultant: true,
      // TEMPORAIRE (PRE-4906) : masquée sauf rôle R_IT tant que la feature n'est pas complète —
      // cf. isTauxParSupportFeatureVisible(), à retirer avant la mise en prod définitive.
      callbackBasedOn: (entity: any) => {
        if (!this.isTauxParSupportFeatureVisible()) {
          return true;
        }

        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return true;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      alwaysShow: (entity, user) => {
        if (!this.isTauxParSupportFeatureVisible()) {
          return false;
        }

        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return false;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'versementsLibresFeesForm',
      getTitle(entity: PledgeRead) {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return 'VERSEMENT_PROGRAMME' === operation
          ? 'Frais sur le versement programmé'
          : 'Frais sur le versement libre';
      },
      template: 'versementsLibresFeesForm',
      callbackBasedOn(entity: any) {
        return !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
          entity?.operation,
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getVersementsLibresFeesForm(entity);
      },
      alwaysShow(entity, user) {
        return (
          entity?.operation === 'VERSEMENT_COMPLEMENTAIRE' ||
          entity?.operation === 'VERSEMENT_PROGRAMME'
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'arbitrageDisinvestmentForm',
      title: 'Désinvestissement en stock',
      template: 'arbitrageDisinvestmentForm',
      callbackBasedOn(entity: any) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return true;
        }
        const { products = [] } = entity || {};
        const needRepartitionStock = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'FLUX',
        );

        return needRepartitionStock;
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation =
          this.cBService.getArbitrageDisinvestmentForm(entity);
        const { products = [] } = entity || {};
        const needRepartitionStock = products?.every(
          ({ parameters }) => parameters?.arbitrageType !== 'FLUX',
        );

        if (!needRepartitionStock) {
          return formValidation;
        }

        const hasRepartitionStock =
          !!products?.[0]?.parameters?.supportsDivestment?.length &&
          products[0].parameters?.supportsDivestment?.some(
            ({ repartitionStock }) => repartitionStock && repartitionStock > 0,
          );

        return formValidation && hasRepartitionStock;
      },
      alwaysShow(entity, user) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return false;
        }
        const { products = [] } = entity || {};
        const needRepartitionStock = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'FLUX',
        );

        return !needRepartitionStock;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'arbitrageInvestmentAllocation',
      title: 'Comment souhaitez-vous répartir votre réinvestissement ?',
      subTitle:
        'Différentes formules sont proposées pour composer la nouvelle allocation de votre contrat.',
      template: 'arbitrageInvestmentAllocation',
      type: 'products',
      callbackBasedOn(entity: any) {
        return entity?.operation !== 'ARBITRAGE_INDIVIDUAL';
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const { products = [] } = entity || {};
        return products?.every(
          ({ parameters }) => !!parameters?.arbitrageDistribution,
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'arbitrageSimulationPacks',
      title: 'Choisissez votre allocation type.',
      template: 'arbitrageSimulationPacks',
      type: 'products',
      callbackBasedOn(entity: any) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return true;
        }
        const { products = [] } = entity || {};
        return !products?.every(
          ({ parameters }) => parameters?.arbitrageDistribution === 'PACK',
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const { products = [] } = entity || {};
        return products?.every(({ parameters }) => !!parameters?.productPackId);
      },
      alwaysShow(entity, user) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return false;
        }
        const { products = [] } = entity || {};
        return products?.every(
          ({ parameters }) => parameters?.arbitrageDistribution === 'PACK',
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'reinvestmentStockForm',
      title: 'Réinvestissement en stock',
      template: 'reinvestmentStockForm',
      callbackBasedOn(entity: any) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return true;
        }
        const { products = [] } = entity || {};
        const needRepartitionStock = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'FLUX',
        );

        return needRepartitionStock;
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation = this.cBService.getReinvestmentStockForm(entity);
        const { products = [] } = entity || {};

        const hasRepartitionStock = !!products?.[0]?.parameters
          ?.supportsInvestmentStock?.length
          ? products[0].parameters?.supportsInvestmentStock?.map(
              ({ repartitionStock }) => repartitionStock,
            ) || []
          : [];

        return _.sum(hasRepartitionStock) === 1 && formValidation;
      },
      alwaysShow(entity, user) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return false;
        }
        const { products = [] } = entity || {};
        const needRepartitionStock = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'FLUX',
        );

        return !needRepartitionStock;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'reinvestmentFluxForm',
      title: 'Réinvestissement en flux',
      template: 'reinvestmentFluxForm',
      callbackBasedOn(entity: any) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return true;
        }
        const { products = [] } = entity || {};
        const needRepartitionFlux = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'STOCK',
        );

        return needRepartitionFlux;
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation = this.cBService.getReinvestmentFluxForm(entity);
        const { products = [] } = entity || {};

        const hasRepartitionFLux = !!products?.[0]?.parameters
          ?.supportsInvestmentFlux?.length
          ? products[0].parameters?.supportsInvestmentFlux?.map(
              ({ repartitionFlux }) => repartitionFlux,
            ) || []
          : [];

        return _.sum(hasRepartitionFLux) === 1 && formValidation;
      },
      alwaysShow(entity, user) {
        if (entity?.operation !== 'ARBITRAGE_INDIVIDUAL') {
          return false;
        }
        const { products = [] } = entity || {};
        const needRepartitionFlux = products?.every(
          ({ parameters }) => parameters?.arbitrageType === 'STOCK',
        );

        return !needRepartitionFlux;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      // PRE-4896/PRE-4906 : slide "Taux par support" (arbitrage) — visible uniquement pour un
      // consultant, quelle que soit l'organisation. Éditable pour les ventes en direct ; affichée
      // en lecture seule (champs désactivés, cf. taux-par-support-arbitrage.component.ts et
      // isSaleViaAlbR()) pour toute vente via AlbR (organisation affiliée EUODIA, ou produit sans
      // vente en direct).
      name: 'tauxParSupportArbitrage',
      getTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? 'Vérifiez les commissions par support'
          : 'Saisissez les commissions par support';
      },
      getSubTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? "Vérifiez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, vérifiez l'upfront. En cas d'erreur, contactez les équipes dédiées."
          : "Renseignez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, saisissez uniquement l'upfront.";
      },
      template: 'tauxParSupportArbitrage',
      isConsultant: true,
      // TEMPORAIRE (PRE-4906) : masquée sauf rôle R_IT tant que la feature n'est pas complète —
      // cf. isTauxParSupportFeatureVisible(), à retirer avant la mise en prod définitive.
      callbackBasedOn: (entity: any) => {
        return (
          entity?.operation !== 'ARBITRAGE_INDIVIDUAL' ||
          !this.isTauxParSupportFeatureVisible()
        );
      },
      alwaysShow: (entity, user) => {
        return (
          entity?.operation === 'ARBITRAGE_INDIVIDUAL' &&
          this.isTauxParSupportFeatureVisible()
        );
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatAmountForm',
      title: 'Montant du rachat',
      template: 'rachatAmountForm',
      callbackBasedOn(entity: any) {
        return !isRppOperation(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getRachatAmountForm(entity);
      },
      alwaysShow(entity, user) {
        return isRppOperation(entity?.operation);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatDisinvestmentForm',
      title: 'Désinvestissement',
      getTitle: (entity: PledgeRead) => {
        return isPonctuelProgrammed(entity)
          ? 'Désinvestissement du rachat ponctuel'
          : 'Désinvestissement';
      },
      template: 'rachatDisinvestmentForm',
      callbackBasedOn(entity: any) {
        return !['RACHAT_PARTIEL', 'AVANCE', RPP_OPERATION].includes(
          entity?.operation,
        );
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const formValidation =
          this.cBService.getRachatDisinvestmentForm(entity);
        const { products = [] } = entity || {};

        const supportsDivestment =
          products?.[0]?.parameters?.supportsDivestment || [];

        const hasRepartition = isRppOperation(entity?.operation)
          ? ratiosAreComplete(
              supportsDivestment.map(({ disinvest }) => disinvest),
            )
          : !!supportsDivestment.length &&
            supportsDivestment.some(
              ({ disinvest }) => disinvest && disinvest > 0,
            );

        return formValidation && hasRepartition;
      },
      alwaysShow(entity, user) {
        return ['RACHAT_PARTIEL', 'AVANCE', RPP_OPERATION].includes(
          entity?.operation,
        );
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatProgrammedAmountForm',
      title: 'Montant du rachat programmé',
      template: 'rachatProgrammedAmountForm',
      callbackBasedOn: (entity: any) => {
        return !isPonctuelProgrammed(entity);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getRachatProgrammedAmountForm(entity);
      },
      alwaysShow: (entity: any, user) => {
        return isPonctuelProgrammed(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'rachatProgrammedDisinvestmentForm',
      title: 'Répartition du rachat programmé',
      template: 'rachatProgrammedDisinvestmentForm',
      callbackBasedOn: (entity: any) => {
        return !isPonctuelProgrammed(entity);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        const supportsDivestmentProgrammed =
          entity?.products?.[0]?.parameters?.supportsDivestmentProgrammed || [];

        return (
          this.cBService.getRachatProgrammedDisinvestmentForm(entity) &&
          ratiosAreComplete(
            supportsDivestmentProgrammed.map(({ disinvest }) => disinvest),
          )
        );
      },
      alwaysShow: (entity: any, user) => {
        return isPonctuelProgrammed(entity);
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'perType',
      title: 'Type de PER',
      template: 'perType',
      callbackBasedOn(entity: any) {
        return entity?.operation !== 'SUBSCRIPTION';
      },
      callbackValidation(pledge: PledgeRead) {
        return !_.isNil(pledge?.products?.[0]?.parameters?.perTnsSalarie);
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'perEffectiveDate',
      title: "Date d'effet du PER",
      template: 'perEffectiveDate',
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }
        const mainProduct = products?.[0] || {};
        const mainProductPartner = mainProduct?.parameters?.partner?.slug;

        if (mainProductPartner === 'generali') {
          return false;
        }
        return true;
      },
      callbackValidation(pledge: PledgeRead) {
        return !!pledge?.products?.[0]?.parameters?.perEffectiveDate;
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'scpiOwnershipType',
      template: 'scpiOwnershipType',
      callbackValidation(pledge: PledgeRead) {
        return pledge?.parameters?.scpiOwnershipType ? true : false;
      },

      title: 'Vous souhaitez effectuer cette souscription :',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'selectSCPI',
      template: 'selectSCPI',
      title: 'Comment souhaitez vous sélectionner vos SCPI ?',
      type: 'owners',
      callbackBasedOn(pledge: PledgeRead) {
        return ['NU_PROPRIO', 'USUFRUIT'].includes(
          pledge?.parameters?.scpiOwnershipType,
        );
      },
      callbackValidation(pledge: PledgeRead) {
        if (!pledge?.parameters?.scpiSelectionType) {
          return false;
        }
        return !!pledge?.parameters?.scpiOwnershipType ? true : false;
      },
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'productForm',
      template: 'productForm',
      callbackValidation(entity: PledgeRead) {
        return entity?.products?.length > 0;
      },
      title: 'Veuillez sélectionner vos produits',
      type: 'owners',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'SCPIConfiguration',
      template: 'SCPIConfiguration',
      title: 'Choisissez vos SCPI préférées',
      type: 'products',
      callbackValidation(entity: PledgeRead) {
        return entity?.products?.every(
          product => product?.parameters?.scpiNbPart,
        );
      },
      callbackBasedOn(pledge: PledgeRead) {
        return pledge?.parameters?.scpiSelectionType !== 'FREE';
      },
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'scpiAmount',
      template: 'scpiAmount',
      title: 'Quel montant souhaitez-vous investir ?',
      subTitle:
        'Le montant investi pourra légèrement être modifié en fonction du prix des parts.',
      type: 'parameters',
      callbackBasedOn(pledge: PledgeRead) {
        return pledge?.parameters?.scpiSelectionType == 'FREE';
      },
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },

    {
      name: 'scpiTable',
      template: 'scpiTable',
      title: '',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'riskLevelExpected',
      template: 'riskLevelExpected',
      title: "Niveau de risque de l'investissement",
      category: 'Composition du contrat',
      productType: ['SCPI', 'CROWDFUNDING'],
      callbackValidation: (entity: PledgeRead) => {
        return !!entity?.parameters?.riskLevelExpected;
      },
    },
    {
      name: 'maximumLostPercent',
      template: 'maximumLostPercent',
      title: 'Quel montant de votre investissement êtes-vous prêt(e) à perdre',
      callbackValidation: (entity: PledgeRead) =>
        !!entity?.parameters?.maximumLostPercent,
      callbackBasedOn: (entity: PledgeRead) =>
        !entity?.parameters?.additionalQuestions?.find(
          q => q.id === CompanyFieldType.MAXIMUM_LOST_PERCENT,
        )
          ? true
          : false,

      category: 'Composition du contrat',
      productType: ['PRIVATE_EQUITY'],
    },
    {
      name: 'riskTotalLoss',
      template: 'riskTotalLoss',
      title: 'Risque de perte total',
      callbackValidation: (entity: PledgeRead) => {
        return !!entity?.parameters?.riskTotalLoss;
      },
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },

    //  her
    {
      name: 'realEstateCharacteristicsCristalis',
      template: 'realEstateCharacteristicsCristalis',
      title: 'Caractéristiques de votre bien immobilier',
      type: 'products',
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getRealEstateCharacteristics(entity, user);
      },
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
    },
    {
      name: 'enrollmentOffice',
      template: 'enrollmentOffice',
      title: "Votre caisse d'affiliation ?",
      type: 'products',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead, user) => {
        return (
          !!entity?.products?.[0]?.parameters?.enrollmentOffice ||
          !!user.sessionConsultant
        );
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return false;
      },
      productType: ['PRESTATION'],
    },
    {
      name: 'jointOwnershipChoice',
      template: 'jointOwnershipChoice',
      title: 'S’agit-il d’une indivision ?',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return (
          pledge.products?.[0].productName !== 'Kit Particulier' ||
          (user?.sessionConsultant ? false : true)
        );
      },
    },
    {
      name: 'jointOwnership',
      template: 'jointOwnership',
      title: 'Ajoutez vos indivisaires',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackValidation: (entity, user) => {
        return this.cBService.getIndivisaire(entity, user);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return (
          !pledge.products?.[0]?.parameters?.prestationJointOwnershipChoice ||
          pledge.products?.[0].productName !== 'Kit Particulier'
        );
      },
    },
    {
      name: 'missionParticulier',
      template: 'missionParticulier',
      title: 'De quelles missions complémentaires avez-vous besoin ?',

      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        const { productName } = pledge?.products[0] || {};
        if (!productName?.includes('Particulier')) return true;
        return false;
      },
    },
    {
      name: 'missionLegalEntity',
      template: 'missionLegalEntity',
      title: 'De quelles missions complémentaires avez-vous besoin ?',

      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        const { productName } = pledge?.products[0] || {};
        if (productName?.includes('Particulier')) return true;
        return false;
      },
    },
    {
      name: 'optionParticulier',
      template: 'optionParticulier',
      title: 'De quelles options avez-vous besoin ?',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        const { productName } = pledge?.products[0] || {};
        if (!productName?.includes('Particulier')) return true;
        return false;
      },
    },
    {
      name: 'optionLegalEntity',
      template: 'optionLegalEntity',
      title: 'De quelles options avez-vous besoin ?',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        const { productName } = pledge?.products[0] || {};
        if (productName?.includes('Particulier')) return true;
        return false;
      },
    },
    {
      name: 'startDateRent',
      template: 'startDateRent',
      title: 'Date de première mise en location',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },
    {
      name: 'startDateMission',
      template: 'startDateMission',
      title: "Date de début d'exercice",
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },
    {
      name: 'finalDateMission',
      template: 'finalDateMission',
      title: "Date de fin d'exercice",
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },
    // {
    //   name: "finalDateMissionCustom",
    //   template: "finalDateMissionCustom",
    //   title: "Dates d'exercices",
    //   type: "products",
    //   category: "Composition du contrat",
    //   productType: ["PRESTATION"],
    //   callbackBasedOn: (pledge: PledgeRead, user) => {
    //     return pledge.products?.[0].productName !== "Kit Particulier" || (user?.sessionConsultant ? true : false);
    //   },
    // },
    {
      name: 'cgaSituation',
      template: 'cgaSituation',
      title:
        "Souscrire à un CGA pour bénéficier d'une réduction d'impôt de 2/3 de nos honoraires ?",
      subTitle:
        'Nous nous chargeons de transmettre votre dossier au Centre de Gestion Agréé OGI France. Celui-ci gérera votre dossier et vous facturera directement leurs honoraires de 50€ TTC la première année.',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },
    {
      name: 'tradeDiscount',
      template: 'tradeDiscount',
      title: 'Remise commerciale ',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },
    {
      name: 'paymentFrequency',
      template: 'paymentFrequency',
      title: 'Fréquence de paiement ',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        return !user?.sessionConsultant;
      },
    },

    {
      name: 'invest',
      template: 'invest',
      callbackValidation: entity => {
        return this.cBService.getInvest(entity);
      },
      callbackBasedOn(entity: any, user) {
        return entity?.operation !== 'SUBSCRIPTION';
      },
      alwaysShow(entity: any, user) {
        return entity?.operation === 'SUBSCRIPTION';
      },
      title: 'Comment souhaitez-vous investir ?',
      subTitle:
        "Pour réaliser le contrat, nous devons établir votre plan d'investissement.",
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'fiscalityTypeForm',
      title: 'Fiscalité PER',
      template: 'fiscalityTypeForm',
      callbackBasedOn(entity: any) {
        return ![
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
          'SUBSCRIPTION',
        ].includes(entity?.operation);
      },
      callbackValidation: (entity: PledgeRead, user) => {
        return this.cBService.getFiscalityTypeForm(entity);
      },
      alwaysShow(entity, user) {
        return (
          entity?.operation === 'VERSEMENT_COMPLEMENTAIRE' ||
          entity?.operation === 'SUBSCRIPTION' ||
          entity?.operation === 'VERSEMENT_PROGRAMME'
        );
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'corumVersementForm',
      template: 'corumVersementForm',
      callbackValidation: entity => {
        const { products = [] } = entity || {};
        return products?.[0]?.parameters?.supportsInvestment?.totalAmount > 0;
      },
      callbackBasedOn(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return true;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          !['VERSEMENT_COMPLEMENTAIRE', 'VERSEMENT_PROGRAMME'].includes(
            operation,
          )
        ) {
          return false;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      title: 'Montant du versement libre Corum',
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PER',
      ],
    },
    {
      name: 'corumProfilesForm',
      template: 'corumProfilesForm',
      callbackValidation: entity => {
        const { products = [] } = entity || {};
        const corumProfilesPercentage =
          products?.[0]?.parameters?.corumProfiles?.map(
            ({ percentage }) => percentage,
          ) || [];

        return _.sum(corumProfilesPercentage) === 100;
      },
      callbackBasedOn(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
          ].includes(operation)
        ) {
          return true;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
          ].includes(operation)
        ) {
          return false;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      title: 'Corum allocation profiles',
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      name: 'corumGestionLibreForm',
      template: 'corumGestionLibreForm',
      callbackValidation: entity => {
        const { products = [] } = entity || {};
        const corumGestionLibreRepartitionPercentage =
          products?.[0]?.parameters?.corumGestionLibreRepartition?.map(
            ({ percentage }) => percentage,
          ) || [];

        return _.sum(corumGestionLibreRepartitionPercentage) === 100;
      },
      callbackBasedOn(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
          ].includes(operation)
        ) {
          return true;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        if (!hasCorumPartnerProduct) {
          return true;
        }

        const percentageCorumGestionLibre =
          products?.[0]?.parameters?.corumProfiles?.find(
            ({ isin }) => isin === 'gestion-libre',
          )?.percentage || 0;

        return percentageCorumGestionLibre === 0;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
          ].includes(operation)
        ) {
          return false;
        }

        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        if (!hasCorumPartnerProduct) {
          return false;
        }

        const percentageCorumGestionLibre =
          products?.[0]?.parameters?.corumProfiles?.find(
            ({ isin }) => isin === 'gestion-libre',
          )?.percentage || 0;

        return percentageCorumGestionLibre > 0;
      },
      title: 'Corum répartition gestion libre',
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },

    {
      id: 45,
      name: 'investmentDuration',
      step: '45',
      template: 'investmentDuration',
      callbackValidation: entity => {
        return this.cBService.getInvestmentDuration(entity);
      },
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION' } = entity || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
            'ARBITRAGE_INDIVIDUAL',
          ].includes(operation)
        ) {
          return true;
        }

        return false;
      },
      title: "Quelle durée d'investissement envisagez-vous ?",
      type: 'owners',
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'userNifCode',
      template: 'userNifCode',
      callbackValidation: (entity, user) => {
        return this.cBService.getNifCode(entity, user);
      },
      title: 'Votre numéro fiscal',
      type: 'parameters',
      callbackBasedOn(pledge: PledgeRead) {
        const {
          operation = 'SUBSCRIPTION',
          scope = 'FULL',
          productType = '',
          products = [],
        } = pledge || {};

        const hasFinancileProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'FINANCILE',
        );

        if (scope === 'LEGAL_ONLY' && !hasFinancileProduct) {
          return true;
        }

        if (productType === 'PRESTATION') return false;
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.NIF_CODE,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'spouseInfo',
      template: 'spouseInfo',
      callbackValidation: entity => {
        return this.cBService.getSpouseInfo(entity);
      },
      title: 'Informations sur votre conjoint(e)',
      subTitle:
        'Vous êtes marié(e) sous un régime communautaire, nous avons besoin de quelques informations sur votre conjoint(e).',
      type: 'owners',
      physicalUserOnly: true,
      callbackBasedOn(pledge: PledgeRead) {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.SPOUSE_INFORMATION,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [],
    },
    //subscribeInLife
    // {
    //   id: 41,
    //   name: "subscribeInLife",
    //   step: "41",
    //   template: "subscribeInLife",
    //   callbackValidation: (entity) => {
    //     return this.cBService.getSubscribeInLife(entity);
    //   },
    //   title: "Voulez-vous souscrire en viager ?",
    //   type: "parameters",
    // },
    {
      name: 'swisslifeRenonceProfil',
      template: 'swisslifeRenonceProfil',
      subTitle:
        'Je renonce expressément, conformément à la possibilité qui m’est donnée par l’article L.224-3 du code monétaire et financier, à l’investissement des primes qui seront versées dans le cadre de mon adhésion au contrat SwissLife PER individuel au sein de l’allocation par défaut « Pilotage Retraite Equilibré ».',
      getSubTitle(entity: PledgeRead) {
        const isAlbrPer = (entity?.products ?? []).some(
          p => p?.productId === PER_ALBR_PRODUCT_ID,
        );
        return isAlbrPer
          ? 'Je renonce expressément, conformément à la possibilité qui m’est donnée par l’article L.224-3 du code monétaire et financier, à l’investissement des primes qui seront versées dans le cadre de mon adhésion au contrat PER albR au sein de l’allocation par défaut « Profil 2 ».'
          : 'Je renonce expressément, conformément à la possibilité qui m’est donnée par l’article L.224-3 du code monétaire et financier, à l’investissement des primes qui seront versées dans le cadre de mon adhésion au contrat SwissLife PER individuel au sein de l’allocation par défaut « Pilotage Retraite Equilibré ».';
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { products = [] } = pledge || {};
        const hasSwisslifePartner = products.some(
          ({ parameters }) => parameters?.partnerFlux === 'SWISSLIFE',
        );
        if (
          pledge?.operation !== 'SUBSCRIPTION' ||
          pledge?.productType !== 'PER' ||
          !hasSwisslifePartner
        ) {
          return true;
        }

        return false;
      },
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getSwisslifeRenonceProfil(entity);
      },
      title: 'Allocation de gestion par défaut',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      id: 47,
      name: 'investmentAllocation',
      step: '47',

      template: 'investmentAllocation',
      // callbackValidation: (entity) => {
      //   return this.cBService.getInvestmentAllocation(entity);
      // },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return false;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      title: 'Comment souhaitez-vous répartir votre investissement ?',
      subTitle:
        "Différentes formules sont proposées pour vous aider à composer l'allocation de votre contrat.",
      type: 'products',
      category: 'Composition du contrat',
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'support',

      title: 'Choisissez librement vos supports.',
      getTitle: (entity: PledgeRead) => {
        return entity?.products?.[0]?.parameters?.avDistribution === 'PACK'
          ? 'Choisissez votre pack.'
          : 'Choisissez librement vos supports.';
      },
      template: 'support',
      callbackValidation: entity => {
        return this.cBService.getSupport(entity);
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return false;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumPartnerProduct;
      },
      type: 'products',
      callbackBasedOn(entity: any) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumPartnerProduct;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'isinTable',

      template: 'isinTable',
      callbackValidation: entity => {
        return this.cBService.getIsinTable(entity);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', products = [] } = pledge || {};
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );

        if (hasCorumPartnerProduct) {
          return true;
        }

        return !(
          pledge?.products?.[0]?.parameters?.avDistribution !== 'PILOTED'
        );
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', products = [] } = entity || {};
        if (operation !== 'SUBSCRIPTION') {
          return false;
        }
        const hasCorumPartnerProduct = products?.some(
          product => product?.parameters?.partnerFlux === 'CORUM',
        );
        if (hasCorumPartnerProduct) {
          return false;
        }

        return products?.[0]?.parameters?.avDistribution !== 'PILOTED';
      },
      title:
        'Ajustez la répartition des montants à l’intérieur de votre contrat.',
      subTitle:
        'Vérifiez ou modifiez la répartition des versements dans vos différents supports.',
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      // PRE-4896/PRE-4906 : slide "Taux par support" — visible uniquement pour un consultant
      // (isConsultant), quelle que soit l'organisation. Éditable pour les ventes en direct ;
      // affichée en lecture seule (champs désactivés, cf. taux-par-support.component.ts et
      // isSaleViaAlbR()) pour toute vente via AlbR (organisation affiliée EUODIA, ou produit sans
      // vente en direct) — la variation de comportement se joue dans le composant, pas dans la
      // visibilité de la Question elle-même.
      name: 'tauxParSupport',
      // TEMPORAIRE (PRE-4906) : masquée sauf rôle R_IT tant que la feature n'est pas complète —
      // cf. isTauxParSupportFeatureVisible(), à retirer avant la mise en prod définitive.
      callbackBasedOn: (entity: any) => {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return (
          operation !== 'SUBSCRIPTION' || !this.isTauxParSupportFeatureVisible()
        );
      },
      alwaysShow: (entity: any, user) => {
        const { operation = 'SUBSCRIPTION' } = entity || {};
        return (
          operation === 'SUBSCRIPTION' && this.isTauxParSupportFeatureVisible()
        );
      },
      getTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? 'Vérifiez les commissions par support'
          : 'Saisissez les commissions par support';
      },
      getSubTitle: (entity: PledgeRead, user?: UserAuthentified) => {
        return isSaleViaAlbR(user?.organizationId, entity?.products)
          ? "Vérifiez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, vérifiez l'upfront. Ces données alimentent le tableau des coûts et frais MIF2/DDA. En cas d'erreur, contactez les équipes dédiées."
          : "Renseignez les frais de gestion support et la rétrocession assureur pour les UC/Fonds Euros. Pour les produits structurés, saisissez uniquement l'upfront. Ces données alimentent le tableau des coûts et frais MIF2/DDA.";
      },
      template: 'tauxParSupport',
      isConsultant: true,
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'garantiePlancher',
      template: 'garantiePlancher',
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }

        return false;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = entity || {};
        return operation === 'SUBSCRIPTION' && scope !== 'LEGAL_ONLY';
      },
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getGarantiePlancher(entity);
      },
      title: 'Souhaitez-vous souscrire une garantie plancher ?',
      subTitle:
        'Garantissez à vos bénéficiaires un capital minimum en cas de décès.',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['ASSURANCE_VIE', 'CONTRAT_CAPI', 'PEI', 'PER'],
    },
    {
      name: 'exonerationArretTravail',
      template: 'exonerationArretTravail',
      subTitle:
        'En cas de souscription de l’option Garantie « Exonération en cas d’arrêt de travail », joindre impérativement à la demande d’adhésion le questionnaire de santé (Modèle 12305). L’adhérent est informé qu’en signant ce document, il consent au traitement de ses données de santé par l’assureur pour les stricts besoins liés à l’exécution de son contrat.',
      callbackBasedOn(pledge: PledgeRead) {
        const { products = [] } = pledge || {};
        const hasSwisslifePartner = products.some(
          ({ parameters }) => parameters?.partnerFlux === 'SWISSLIFE',
        );
        const isPerTns = products?.[0]?.parameters?.perTnsSalarie === 'TNS';
        console.log({ isPerTns });
        if (
          pledge?.operation !== 'SUBSCRIPTION' ||
          pledge?.productType !== 'PER' ||
          !hasSwisslifePartner ||
          !isPerTns
        ) {
          return true;
        }

        return false;
      },
      callbackValidation: (entity: PledgeRead) => {
        return this.cBService.getExonerationArretTravail(entity);
      },
      title:
        'Souhaitez-vous souscrire à la garantie exonération en cas d’arrêt de travail ?',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      id: 51,
      name: 'optionConfiguration',
      step: '51',

      template: 'optionConfiguration',

      // alwaysShow: true,
      title: 'Souhaitez-vous une option ?',
      subTitle:
        'Les options permettent d’affiner votre contrat et vous en donnent la pleine maîtrise. Plusieurs options peuvent être choisies.',
      type: 'products',
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !(pledge?.products?.[0]?.parameters?.partnerFlux !== 'CORUM');
      },
      category: 'Composition du contrat',
      productType: ['ASSURANCE_VIE', 'CONTRAT_CAPI', 'PEI', 'PER'],
    },
    {
      name: 'perOptionConfiguration',

      template: 'perOptionConfiguration',

      title: 'souhaitez-vous une option 2/2',

      type: 'products',
      callbackBasedOn(pledge: PledgeRead) {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PER_GARANTIE_BONNE_FIN,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'perDeductionConfiguration',
      template: 'perDeductionConfiguration',
      title:
        'Souhaitez-vous que vos versements soient déductibles de votre revenu imposable ?',
      type: 'products',
      callbackBasedOn(pledge: PledgeRead) {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PER_DEDUCTION_IMPOTS,
        )
          ? true
          : false;
      },
      callbackValidation: (pledge: PledgeRead) => {
        const hasQuestion = pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PER_DEDUCTION_IMPOTS,
        );

        if (!hasQuestion) {
          return true;
        }

        const product = pledge?.products?.[0];
        const investmentType = product?.parameters?.avInvestmentType;
        if (investmentType === 'MONTHLY') {
          return !_.isNil(
            product?.parameters?.perEnabledTaxDeductibilityProgrammedInvestment,
          );
        } else if (investmentType === 'INITIAL') {
          return !_.isNil(
            product?.parameters?.perEnabledTaxDeductibilityInitialInvestment,
          );
        } else if (investmentType === 'INITIAL_AND_MONTHLY') {
          return (
            !_.isNil(
              product?.parameters?.perEnabledTaxDeductibilityInitialInvestment,
            ) &&
            !_.isNil(
              product?.parameters
                ?.perEnabledTaxDeductibilityProgrammedInvestment,
            )
          );
        }

        return false;
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'perExitConfiguration',
      template: 'perExitConfiguration',
      title:
        'Souhaitez-vous une sortie en rente viagère ou en capital pour votre PER ?',
      type: 'products',
      callbackBasedOn(pledge: PledgeRead) {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PER_SORTIE,
        )
          ? true
          : false;
      },
      callbackValidation: (pledge: PledgeRead) => {
        const hasQuestion = pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PER_SORTIE,
        );

        if (!hasQuestion) {
          return true;
        }

        const product = pledge?.products?.[0];
        return !_.isNil(product?.parameters?.perExitType);
      },
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'optionEpargneHandicap',
      template: 'optionEpargneHandicap',
      title: 'Souhaitez-vous une option Epargne Handicap ?',
      type: 'products',
      callbackBasedOn(pledge: PledgeRead) {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.OPTION_EPARGNE_HANDICAP,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      id: 52,
      name: 'investmentAmountDuration',
      step: '52',
      title: 'Configurez votre investissement progressif.',
      subTitle:
        "L'investissement progressif pemet d'entrer sur le marché au long du temps afin de bénéficier de fluctuations favorables depuis le fonds euros nouvelle génération vers une sélection d'unité de comptes.",
      type: 'products',
      template: 'investmentAmountDuration',
      callbackValidation: pledge => {
        const { products = [] } = pledge || {};
        const { parameters = {} } = products?.[0] || {};
        const {
          avOptionInvestissementProgressifMontantArbitrage,
          avOptionInvestissementProgressifDuree,
        } = parameters;
        return (
          !_.isNil(avOptionInvestissementProgressifMontantArbitrage) &&
          !_.isNil(avOptionInvestissementProgressifDuree)
        );
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return (
          pledge?.products &&
          !pledge?.products?.[0]?.parameters?.avOptionInvestissementProgressif
        );
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      id: 53,
      name: 'investissementSupportForm',
      step: '53',

      template: 'investissementSupportForm',
      title: 'Configurez votre investissement progressif.',
      subTitle:
        'Vérifiez ou modifiez la répartition des versements dans vos différents supports.',
      type: 'products',
      callbackValidation: pledge => {
        const { products = [] } = pledge || {};
        const { parameters = {} } = products?.[0] || {};
        const { avOptionInvestissementProgressifRepartition } = parameters;
        return (
          !_.isNil(avOptionInvestissementProgressifRepartition) &&
          avOptionInvestissementProgressifRepartition.length > 0
        );
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return (
          pledge?.products &&
          !pledge?.products?.[0]?.parameters?.avOptionInvestissementProgressif
        );
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      id: 54,
      name: 'investissementProgressifTable',
      step: '54',

      template: 'investissementProgressifTable',
      title: 'Configurez votre investissement progressif.',
      subTitle:
        'Vérifiez ou modifiez la répartition des versements dans vos différents supports.',
      type: 'products',
      callbackValidation: pledge => {
        const { products = [] } = pledge || {};
        const { parameters = {} } = products?.[0] || {};
        const { avOptionInvestissementProgressifRepartition } = parameters;
        return (
          !_.isNil(avOptionInvestissementProgressifRepartition) &&
          avOptionInvestissementProgressifRepartition.length > 0 &&
          _.sumBy(
            avOptionInvestissementProgressifRepartition,
            'repartition',
          ) === 1
        );
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return (
          pledge?.products &&
          !pledge?.products?.[0]?.parameters?.avOptionInvestissementProgressif
        );
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      id: 55,
      name: 'securisationPlus',
      step: '55',

      template: 'securisationPlus',
      title: 'Configurez la sécurisation des plus-values.',
      subTitle:
        'Dès qu’un seuil prédéterminé de plus-values est dépassé, ces dernières sont reversées sur le fonds en euros nouvelle génération.',
      type: 'products',
      // callbackValidation: (entity) => {
      //   return this.cBService.getSecurisationPlus(entity);
      // },
      callbackValidation: pledge => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return (
          pledge?.products &&
          !pledge?.products?.[0]?.parameters?.avOptionSecurisationPlusValues
        );
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      id: 56,
      name: 'securisationMoinsValues',
      step: '56',
      template: 'securisationMoinsValues',
      title: 'Configurez la limitation des moins-values.',
      subTitle:
        'Dès qu’un seuil prédéterminé de moins-values est atteint, le versement de la totalité d’une unité de compte est déclenché sur le fonds en euros nouvelle génération.Le seuil n’est pas calculé par rapport à la valeur initiale de l’investissement mais par rapport au plus haut niveau atteint.',
      type: 'products',
      // callbackValidation: (entity) => {
      //   return this.cBService.getSecurisationMoinsValues(entity);
      // },
      callbackValidation: pledge => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        if (pledge?.operation !== 'SUBSCRIPTION') {
          return true;
        }

        return (
          pledge?.products &&
          !pledge?.products?.[0]?.parameters
            ?.avOptionLimitationMoinsValuesRelatives
        );
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },

    {
      id: 57,
      name: 'beneficiary',
      step: '57',

      template: 'beneficiary',
      callbackValidation: entity => {
        return this.cBService.getBeneficiary(entity);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }
        return false;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = entity || {};
        return operation === 'SUBSCRIPTION' && scope !== 'LEGAL_ONLY';
      },
      title: 'Choisissez votre clause bénéficiaire.',
      subTitle:
        'Au décès de l’assuré, le contrat est dénoué et bénéficie aux personnes désignées dans la clause.',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['ASSURANCE_VIE', 'PEI', 'PER'],
    },
    {
      name: 'swisslifeGestionFinanciere',
      template: 'swisslifeGestionFinanciere',
      callbackValidation: entity => {
        return this.cBService.getSwisslifeGestionFinanciere(entity);
      },
      callbackBasedOn(entity: any) {
        const { products = [] } = entity || {};
        const hasSwisslifePartner = products.some(
          ({ parameters }) => parameters?.partnerFlux === 'SWISSLIFE',
        );

        return entity?.operation !== 'SUBSCRIPTION' || !hasSwisslifePartner;
      },
      alwaysShow(entity: any, user) {
        const { products = [] } = entity || {};
        const hasSwisslifePartner = products.some(
          ({ parameters }) => parameters?.partnerFlux === 'SWISSLIFE',
        );
        return entity?.operation === 'SUBSCRIPTION' && hasSwisslifePartner;
      },
      title: 'Objectif de gestion financière',
      subTitle: 'Objectif de gestion financière de l’adhérent',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PER'],
    },
    {
      name: 'paymentPrimes',
      template: 'paymentPrimes',
      callbackValidation: entity => {
        return this.cBService.getPaymentPrimes(entity);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }

        return false;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = entity || {};
        return operation === 'SUBSCRIPTION' && scope !== 'LEGAL_ONLY';
      },
      title: 'Paiement des primes',
      subTitle: 'Payeur de primes',
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'rentPaymentIban',
      step: 'rentPaymentIban',
      template: 'rentPaymentIban',
      callbackValidation: entity => {
        return this.cBService.getRentPaymentForm(entity);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        if (operation !== 'SUBSCRIPTION') {
          return true;
        }

        return false;
      },
      title: 'Paiement des loyers',
      subTitle: "C'est sur ce compte que seront versé vos loyers",
      getTitle: (entity: PledgeRead) =>
        entity?.productType === 'SCPI'
          ? 'Paiement des loyers'
          : 'Informations bancaires',
      getSubTitle: (entity: PledgeRead) =>
        entity?.productType === 'SCPI'
          ? "C'est sur ce compte que seront versé vos loyers"
          : 'Coordonnées du compte bancaire associé au contrat',
      type: 'owners',
      category: 'Composition du contrat',
      productType: [
        'SCPI',
        'CROWDFUNDING',
        'ASSURANCE_VIE',
        'PER',
        'CONTRAT_CAPI',
      ],
    },
    {
      id: 70,
      name: 'contigencyRegimeProfessionComponent',
      template: 'contigencyRegimeProfessionComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getContigencyRegimeProfession(pledge);
      },
      type: 'owners',
      title: 'Hypothèses prévoyance',
      category: 'Composition du contrat',
      productType: ['CONTINGENCY'],
    },
    {
      id: 77,
      name: 'swisslifePrevoyanceComponent',
      template: 'swisslifePrevoyanceComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getSwisslifePrevoyance(pledge);
      },
      // Désactivé : le choix SPE salarié/TNS n'est plus nécessaire
      // callbackBasedOn(entity: PledgeRead) {
      //   const { products = [] } = entity || {};
      //   const hasSwisslifePartner = products.some(
      //     ({ parameters }) => parameters?.partnerFlux === 'SWISSLIFE',
      //   );
      //   return !hasSwisslifePartner;
      // },
      callbackBasedOn() {
        return true;
      },
      type: 'owners',
      title: 'Solution de prévoyance Swisslife',
      category: 'Composition du contrat',
      productType: ['CONTINGENCY'],
    },
    {
      id: 71,
      name: 'contigencyInformationsComponent',
      template: 'contigencyInformationsComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getContigencyInformations(pledge);
      },
      type: 'owners',
      title: 'Informations sur le contrat',
      category: 'Composition du contrat',
      productType: ['CONTINGENCY'],
    },
    // Désactivé : redondant avec les hypothèses prévoyance (renteConjoint, preservationSalaire, etc.)
    // {
    //   name: 'contigencyProtectLovedComponent',
    //   template: 'contigencyProtectLovedComponent',
    //   callbackValidation: (pledge: PledgeRead) => {
    //     return this.cBService.getContigencyProtectLoved(pledge);
    //   },
    //   type: 'owners',
    //   title: 'Prévoyance vos besoins (1/2)',
    //   category: 'Composition du contrat',
    //   productType: ['CONTINGENCY'],
    // },
    // {
    //   name: 'contigencyMaintainIncomeComponent',
    //   template: 'contigencyMaintainIncomeComponent',
    //   callbackValidation: (pledge: PledgeRead) => {
    //     return this.cBService.getContigencyMaintainIncome(pledge);
    //   },
    //   type: 'owners',
    //   title: 'Prévoyance vos besoins (2/2)',
    //   category: 'Composition du contrat',
    //   productType: ['CONTINGENCY'],
    // },
    {
      id: 72,
      name: 'contigencyGarantiesComponent',
      template: 'contigencyGarantiesComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getContigencyGaranties(pledge);
      },
      type: 'owners',
      title: 'Garanties du contrat',
      category: 'Composition du contrat',
      productType: ['CONTINGENCY'],
    },
    {
      id: 73,
      name: 'insuranceHypothesesComponent',
      template: 'insuranceHypothesesComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getInsuranceHypotheses(pledge);
      },
      type: 'owners',
      title: 'Hypothèses mutuelle',
      category: 'Composition du contrat',
      productType: ['INSURANCE'],
    },
    {
      id: 74,
      name: 'insuranceInformationsComponent',
      template: 'insuranceInformationsComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getInsuranceInformations(pledge);
      },
      type: 'owners',
      title: 'Informations sur le contrat',
      category: 'Composition du contrat',
      productType: ['INSURANCE'],
    },
    {
      id: 75,
      name: 'insuranceGarantiesComponent',
      template: 'insuranceGarantiesComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getInsuranceGaranties(pledge);
      },
      type: 'owners',
      title: 'Garanties du contrat',
      category: 'Composition du contrat',
      productType: ['INSURANCE'],
    },
    {
      id: 76,
      name: 'rcdaDevisSelection',
      template: 'rcdaDevisSelection',
      callbackValidation: () => {
        // Valide dès que le consultant a dépassé ce step
        return this.rcdaDevisSelectionVisited;
      },
      callbackBasedOn(entity, user) {
        return !user?.sessionConsultant;
      },
      type: 'owners',
      title: 'Devis alternatifs pour le RCDA',
      category: 'Composition du contrat',
      productType: ['CONTINGENCY', 'INSURANCE'],
    },

    {
      id: 58,
      name: 'paymentForm',
      step: '58',
      template: 'paymentForm',
      callbackValidation: entity => {
        return this.cBService.getPaymentForm(entity);
      },
      title: 'Mode de paiement',
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        return ![
          'SUBSCRIPTION',
          'VERSEMENT_COMPLEMENTAIRE',
          'VERSEMENT_PROGRAMME',
        ].includes(operation);
      },
      // subTitle:
      //   "Rentrez le moyen de paiement que nous allons utiliser pour créer votre contrat .",
      type: 'owners',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',
        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
      ],
    },
    {
      name: 'paymentCristalisForm',
      template: 'paymentCristalisForm',
      callbackValidation: (entity, user) => {
        if (!!user?.sessionConsultant) return true;
        return this.cBService.getPaymentForm(entity);
      },
      title: 'Mode de paiement',
      type: 'owners',
      category: 'Composition du contrat',
      productType: ['PRESTATION'],
    },
    {
      name: 'investmentObjective',
      template: 'investmentObjective',
      callbackValidation: entity => {
        return this.cBService.getInvestmentObjective(entity);
      },
      title: 'Quels sont vos objectifs d’investissement ?',
      subTitle: 'plusieurs choix sont possibles',
      type: 'owners',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
      ],
    },
    {
      name: 'scpiFinanceExperience',
      template: 'scpiFinanceExperience',
      title: 'Expérience professionnelle dans la finance',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.FINANCIAL_EXPERIENCE,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiFinancialInstruments',
      template: 'scpiFinancialInstruments',
      title: 'Sélectionnez les instruments financiers que vous détenez',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.FINANCIAL_INSTRUMENTS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiStockTransactions',
      template: 'scpiStockTransactions',
      title: 'Actions - Montant et Transactions',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.STOCK_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiObligationsTransactions',
      template: 'scpiObligationsTransactions',
      title: 'Obligations - Montant et Transactions',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.OBLIGATIONS_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiMonetairesTransactions',
      template: 'scpiMonetairesTransactions',
      title:
        'Placement de trésorerie / OPCVM Monétaires - Montant et Transactions',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.MONETAIRES_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiStructuredProductsTransactions',
      template: 'scpiStructuredProductsTransactions',
      title:
        'Produits structurés ou instruments financiers complexes - Montant et Transactions',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.STRUCTURED_PRODUCTS_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiIndirectRealEstateTransactions',
      template: 'scpiIndirectRealEstateTransactions',
      title: 'Immobilier Indirect - Montant et Transactions',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.INDIRECT_REAL_ESTATE_PRODUCTS_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiOtherTransactions',
      template: 'scpiOtherTransactions',
      title: 'Autres instruments financiers - Montant et Transactions ',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.OTHER_PRODUCTS_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    /**
     * Slides SCPI Remake Live + GMA Essentials
     *
     **/
    {
      name: 'scpiAverageAmountTransactions',
      template: 'scpiAverageAmountTransactions',
      title: 'Quel est le montant moyen de vos transactions ?',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.AVERAGE_AMOUNT_TRANSACTIONS,
        );
      },
      productType: ['SCPI'],
    },
    {
      name: 'scpiAverageNumberTransactions',
      template: 'scpiAverageNumberTransactions',
      title: 'Nombre opérations sur les 3 dernières années',
      type: 'parameters',
      category: 'Composition du contrat',
      callbackValidation: (entity: PledgeRead) => {
        return true;
      },
      callbackBasedOn(pledge: PledgeRead) {
        return !findAdditionalQuestions(
          pledge,
          CompanyFieldType.AVERAGE_NUMBER_TRANSACTIONS_THREE_YEARS,
        );
      },
      productType: ['SCPI'],
    },
    //

    {
      name: 'scpiDivident',
      template: 'scpiDivident',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getScpiDividentForm(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const { products = [] } = pledge || {};
        const hasProductEnableReinvestDividend = products.some(
          ({ parameters }) =>
            !!parameters?.scpiEnableReinvestissementDividendes,
        );
        return !hasProductEnableReinvestDividend;
      },
      type: 'owners',
      title: 'Réinvestissement des dividendes perçus',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'scpiScheduledPayment',
      template: 'scpiScheduledPayment',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getScpiScheduledPaymentForm(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const { products = [] } = pledge || {};
        const hasProductEnableProgrammedPayment = products.some(
          ({ parameters }) => !!parameters?.scpiEnablePlanEpargneImmobilier,
        );
        return !hasProductEnableProgrammedPayment;
      },
      type: 'owners',
      title: 'Versement programmé',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'scpiPledgeOfShares',
      template: 'scpiPledgeOfShares',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getScpiPledgeOfShares(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const { scpiOwnershipType } = pledge?.parameters;
        return scpiOwnershipType === 'PLEINE_PROPRIETE_CREDIT' ? false : true;
      },
      type: 'owners',
      title: 'Nantissement des parts',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'scpiAlreadyClientOfProduct',
      template: 'scpiAlreadyClientOfProduct',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getScpiAlreadyClientOfProduct(pledge);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }
        return false;
      },
      type: 'owners',
      title: 'Etes-vous déjà client ?',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },
    {
      name: 'discountForm',
      template: 'discountForm',
      title: 'Remise/Parrainage',
      subTitle:
        "Proposer une remise ou un parrainage à votre client. À ne pas confondre avec l'éventuelle offre promotionnelle du partenaire",
      type: 'owners',
      category: 'Composition du contrat',
      callbackValidation: (pledge: PledgeRead) => {
        // Always valid - it's optional
        return true;
      },
      callbackBasedOn: (entity, user) => {
        if (!user?.sessionConsultant) {
          return true;
        }

        return false;
      },
      // isConsultant: true,
      productType: [],
    },
    {
      name: 'rcdaComparaisonProducts',
      template: 'rcdaComparaisonProducts',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRcdaComparaisonProducts(pledge);
      },
      callbackBasedOn(entity, user) {
        const { operation = 'SUBSCRIPTION' } = entity || {};

        if (operation !== 'SUBSCRIPTION') {
          return true;
        }

        return !user?.sessionConsultant;
      },
      type: 'owners',
      title: 'Produits à comparer dans le RCDA',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
        'SCPI',
      ],
    },
    {
      id: 78,
      name: 'rcdaRecommandation',
      template: 'rcdaRecommandation',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRcdaComparaisonProducts(pledge);
      },
      callbackBasedOn(entity, user) {
        if (
          entity?.productType === 'CONTINGENCY' ||
          entity?.productType === 'INSURANCE'
        )
          return true;
        return !user?.sessionConsultant;
      },
      type: 'owners',
      title: 'Recommandation à faire figurer dans le RCDA',
      category: 'Composition du contrat',
      productType: [],
    },
    {
      name: 'pourcentageDeRemiseForm',
      template: 'pourcentageDeRemiseForm',
      callbackValidation: (pledge: PledgeRead) => {
        const { products = [] } = pledge || {};
        return products?.every(
          ({ parameters }) => !_.isNil(parameters?.pourcentageDeRemise),
        );
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (user && !user?.sessionConsultant) return true;
        const {
          products = [],
          operation = 'SUBSCRIPTION',
          productType,
        } = pledge || {};

        if (
          ![
            'SUBSCRIPTION',
            'VERSEMENT_COMPLEMENTAIRE',
            'VERSEMENT_PROGRAMME',
          ].includes(operation)
        ) {
          return true;
        }

        const questionAdditionals =
          pledge?.parameters?.additionalQuestions?.filter(q =>
            [CompanyFieldType.POURCENTAGE_DE_REMISE]?.includes(q.id),
          );

        const hasAdditionnalQuestion = !!questionAdditionals?.length;

        if (!hasAdditionnalQuestion) {
          return true;
        }

        if (operation === 'SUBSCRIPTION' || productType === 'SCPI') {
          return false;
        }

        const partnerFlux = products?.[0]?.parameters?.partnerFlux;

        if (partnerFlux !== 'CORUM') {
          return false;
        }

        const supportsInvestment =
          products?.[0]?.parameters?.supportsInvestment?.supportRepartition ||
          [];

        if (!supportsInvestment?.length) {
          return true;
        }

        const hasScpiSupport = supportsInvestment.some(
          ({ supportType }) => supportType === 'SCPI',
        );

        return !hasScpiSupport;
      },
      type: 'owners',
      title: 'Offre promotionnelle partenaire',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
      ],
    },
    {
      name: 'codeParrainageForm',
      template: 'codeParrainageForm',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (user && !user?.sessionConsultant) return true;
        const {
          products = [],
          operation,
          productType,
          parameters,
        } = pledge || {};

        if (!['SUBSCRIPTION', 'VERSEMENT_COMPLEMENTAIRE'].includes(operation)) {
          return true;
        }

        if (
          ['CONTRAT_CAPI', 'COMPTE_TITRE', 'PEA', 'PER'].includes(productType)
        ) {
          return true;
        }

        if (parameters?.subscriberType === 'COMPANY') {
          return true;
        }

        if (
          productType === 'SCPI' &&
          ['USUFRUIT', 'NU_PROPRIO'].includes(parameters?.scpiOwnershipType)
        ) {
          return true;
        }

        const hasCorumAvailableCodePartenaire = products.some(
          ({ parameters }) =>
            parameters?.computedInitialInvestmentAmount > 2000 &&
            parameters?.partnerFlux === 'CORUM',
        );

        return !hasCorumAvailableCodePartenaire;
      },
      showFirstTime: true,
      alwaysShow: (pledge: PledgeRead, user) => {
        if (user && !user?.sessionConsultant) return false;
        const {
          products = [],
          operation,
          productType,
          parameters,
        } = pledge || {};

        if (!['SUBSCRIPTION', 'VERSEMENT_COMPLEMENTAIRE'].includes(operation)) {
          return false;
        }

        if (
          ['CONTRAT_CAPI', 'COMPTE_TITRE', 'PEA', 'PER'].includes(productType)
        ) {
          return false;
        }

        if (parameters?.subscriberType === 'COMPANY') {
          return false;
        }

        if (
          productType === 'SCPI' &&
          ['USUFRUIT', 'NU_PROPRIO'].includes(parameters?.scpiOwnershipType)
        ) {
          return false;
        }

        const hasCorumAvailableCodePartenaire = products.some(
          ({ parameters }) =>
            parameters?.computedInitialInvestmentAmount > 2000 &&
            parameters?.partnerFlux === 'CORUM',
        );

        return hasCorumAvailableCodePartenaire;
      },
      type: 'owners',
      title: 'Code de parrainage',
      category: 'Composition du contrat',
      productType: ['ASSURANCE_VIE', 'SCPI'],
    },

    {
      name: 'gfvAlreadyAssociateOfProduct',
      template: 'gfvAlreadyAssociateOfProduct',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getGfvAlreadyAssociateOfProduct(pledge);
      },
      callbackBasedOn(pledge: PledgeRead) {
        const { operation = 'SUBSCRIPTION', scope = 'FULL' } = pledge || {};
        if (scope === 'LEGAL_ONLY') {
          return true;
        }

        return false;
      },
      type: 'owners',
      title: 'Etes-vous déjà associé ?',
      category: 'Composition du contrat',
      productType: ['GFV'],
    },
    {
      name: 'gfvNbPartsComponent',
      template: 'gfvNbPartsComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getGfvNbParts(pledge);
      },
      type: 'owners',
      title: 'Combien de parts souhaitez-vous souscrire ?',
      category: 'Composition du contrat',
      productType: ['GFV'],
    },
    {
      name: 'gfiGffNbPartsComponent',
      template: 'gfiGffNbPartsComponent',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getGfvNbParts(pledge);
      },
      type: 'owners',
      title: 'Combien de parts souhaitez-vous souscrire ?',
      category: 'Composition du contrat',
      productType: ['GFI', 'GFF'],
    },

    // until here
    {
      name: 'girardiAmount',
      step: '64',

      template: 'girardiAmount',

      title: ' Montant investissement et montant de la réduction d’impôts ',
      callbackValidation: entity => {
        return this.cBService.getGirardiAmountForm(entity);
      },
      type: 'parameters',
      category: 'Composition du contrat',
      productType: ['GIRARDIN'],
    },
    {
      name: 'crowdfundingAmount',
      step: '64.1',

      template: 'crowdfundingAmount',

      title: ' Montant investissement ',
      callbackValidation: entity => {
        return this.cBService.getCrowdfundingAmountForm(entity);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.CROWDFUNDING_INVESTMENT_AMOUNT,
        )
          ? true
          : false;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },
    {
      name: 'fcpiAmount',
      template: 'fcpiAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPI'],
    },
    {
      name: 'bondAmount',
      template: 'bondAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['BOND'],
    },
    {
      name: 'titresParticipatifsAmount',
      template: 'titresParticipatifsAmount',
      title: 'Montant investissement',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['TITRES_PARTICIPATIFS'],
    },
    {
      name: 'obligationsAmount',
      template: 'obligationsAmount',
      title: 'Montant investissement',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['OBLIGATIONS'],
    },
    {
      name: 'diversificationInvestmentAmount',
      template: 'diversificationInvestmentAmount',
      title: 'Montant investissement',
      callbackValidation: (pledge: PledgeRead) => {
        return (
          !_.isNil(pledge?.products?.[0]?.parameters?.investmentAmount) &&
          pledge?.products?.[0]?.parameters?.investmentAmount > 0
        );
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['DIVERSIFICATION_INVESTMENT', 'SOCIAL_SHARE'],
    },
    {
      name: 'fpciAmount',
      template: 'fpciAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FPCI'],
    },

    {
      name: 'fipAmount',
      template: 'fipAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FIP', 'FIP_CORSE', 'FIP_DOMTOM'],
    },
    {
      name: 'fcprAmount',
      template: 'fcprAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.investmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPR', 'FPS'],
    },
    {
      name: 'catAmount',
      template: 'catAmount',
      title: 'Montant investissement',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.catInvestmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CAT'],
    },

    {
      name: 'privateEquityAmount',
      template: 'privateEquityAmount',
      title: ' Montant investissement ',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters
          ?.privateEquityInvestmentAmount;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRIVATE_EQUITY'],
    },
    {
      name: 'privateEquityProjectDuration',
      template: 'privateEquityProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },

      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRIVATE_EQUITY'],
    },
    {
      name: 'privateEquityFees',
      template: 'privateEquityFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        const { parameters } = pledge || {};
        const params = parameters?.additionalQuestions?.find(
          q => q?.id === CompanyFieldType.PRIVATE_EQUITY_FEES,
        );
        return params?.required === 'MANDATORY' || false;
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (user && !user?.sessionConsultant) return true;
        const questionAdditionals =
          pledge?.parameters?.additionalQuestions?.filter(q =>
            [CompanyFieldType.PRIVATE_EQUITY_FEES]?.includes(q.id),
          );

        return !questionAdditionals?.length ? true : false;
      },

      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRIVATE_EQUITY'],
    },
    {
      name: 'privateEquityProjectType',
      template: 'privateEquityProjectType',
      title: 'Type de projet',
      callbackValidation: (pledge: PledgeRead) => {
        const { products } = pledge || {};
        return !!products?.[0]?.parameters?.crowdfundingProjectType;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const questionAdditionals =
          pledge?.parameters?.additionalQuestions?.filter(q =>
            [CompanyFieldType.CROWDFUNDING_ACTIONS_OBLIGATIONS]?.includes(q.id),
          );

        return !questionAdditionals?.length ? true : false;
      },

      type: 'products',
      category: 'Composition du contrat',
      productType: ['PRIVATE_EQUITY'],
    },
    {
      name: 'fipProjectDuration',
      template: 'fipProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },

      type: 'products',
      category: 'Composition du contrat',
      productType: ['FIP', 'FIP_CORSE', 'FIP_DOMTOM'],
    },
    {
      name: 'crowdfundingSettings',
      step: '64.2',

      template: 'crowdfundingSettings',

      title: 'Parametrage du Crowdfunding',
      callbackValidation: (pledge: PledgeRead) => {
        const {
          crowdfundingNominatifAdministredOption,
          crowdfundingNominatifPur,
          crowdfundingInsightChoice,
        } = pledge?.products?.[0]?.parameters || {};

        const valid =
          crowdfundingNominatifPur === 'ADMINISTRED'
            ? !!crowdfundingNominatifPur &&
              !!crowdfundingNominatifAdministredOption &&
              !!crowdfundingInsightChoice
            : !!crowdfundingNominatifPur && !!crowdfundingInsightChoice;
        return valid;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const questionAdditionals =
          pledge?.parameters?.additionalQuestions?.filter(q =>
            [
              CompanyFieldType.CROWDFUNDING_NOMINATIF_PUR,
              CompanyFieldType.CROWDFUNDING_INSIGHT_CHOICE,
            ]?.includes(q.id),
          );

        return !questionAdditionals?.length ? true : false;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },
    {
      name: 'fcpiSettings',

      template: 'fcpiSettings',

      title: 'Parametrage du Fcpi',
      callbackValidation: entity => {
        return this.cBService.getFcpiSettingsForm(entity);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const res = !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.FCPI_DELIVERY_MODE,
        )
          ? true
          : false;

        return res;
      },

      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPI'],
    },
    {
      name: 'crowdfundingSettings2',
      step: '64.3',

      template: 'crowdfundingSettings2',

      title: 'Parametrage du Crowdfunding 2',
      callbackValidation: entity => {
        return this.cBService.getCrowdfundingSettings2Form(entity);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const {
          crowdfundingNominatifPur,
          crowdfundingNominatifAdministredOption,
        } = pledge.products?.[0]?.parameters || {};
        const crowdfundingType =
          crowdfundingNominatifPur == 'ADMINISTRED' &&
          ['PEA_PME'].includes(crowdfundingNominatifAdministredOption);
        if (!crowdfundingType) return true;

        const res = !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.CROWDFUNDING_NOMINATIF_PUR,
        )
          ? true
          : false;

        return res;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },
    // {
    //   name: "fcpiSettings2",
    //   template: "fcpiSettings2",
    //   title: "Parametrage du fcpi 2",
    //   callbackValidation: (entity) => {
    //     return this.cBService.getFcpiSettings2Form(entity);
    //   },
    //   callbackBasedOn: (pledge: PledgeRead) => {
    //     const { fcpiNominatifPur, fcpiNominatifAdministredOption } =
    //       pledge.products?.[0] || {};
    //     const fcpiType =
    //       fcpiNominatifPur == "ADMINISTRED" &&
    //       [ "PEA_PME"].includes(fcpiNominatifAdministredOption);
    //     if (!fcpiType) return true;
    //     return false;
    //   },
    //   type: "products",
    //   category: "Composition du contrat",
    //   productType: ["FCPI","FCPR"],
    // },

    {
      name: 'crowdfundingProjectDuration',
      step: '64.5',

      template: 'crowdfundingProjectDuration',

      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.crowdfundingProjectDuration;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.CROWDFUNDING_PROJECT_DURATION,
        )
          ? true
          : false;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },
    {
      name: 'fcpiProjectDuration',
      template: 'fcpiProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPI'],
    },
    {
      name: 'bondProjectDuration',
      template: 'bondProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['BOND'],
    },
    {
      name: 'titresParticipatifsProjectDuration',
      template: 'titresParticipatifsProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['TITRES_PARTICIPATIFS'],
    },
    {
      name: 'diversificationInvestmentProjectDuration',
      template: 'diversificationInvestmentProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !_.isNil(pledge?.parameters?.investmentDuration);
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['DIVERSIFICATION_INVESTMENT', 'SOCIAL_SHARE'],
    },
    {
      name: 'fpciProjectDuration',
      template: 'fpciProjectDuration',
      title: 'Durée du projet',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.parameters?.investmentDuration;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FPCI'],
    },
    {
      name: 'crowdfundingFees',
      step: '64.6',
      template: 'crowdfundingFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getCrowdfundingFee(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (user && !user?.sessionConsultant) return true;
        const questionAdditionals =
          pledge?.parameters?.additionalQuestions?.filter(q =>
            [
              CompanyFieldType.CROWDFUNDING_FEES,
              CompanyFieldType.CROWDFUNDING_INCLUDE_FEES_IN_SUBSCRIPTION_AMOUNT,
            ]?.includes(q.id),
          );

        return !questionAdditionals?.length ? true : false;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['CROWDFUNDING'],
    },
    {
      name: 'fcpiFees',
      template: 'fcpiFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.fees;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPI'],
    },
    {
      name: 'bondFees',
      template: 'bondFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.fees;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['BOND'],
    },
    {
      name: 'titresParticipatifsFees',
      template: 'titresParticipatifsFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.fees;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['TITRES_PARTICIPATIFS'],
    },
    {
      name: 'diversificationInvestmentFees',
      template: 'diversificationInvestmentFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !_.isNil(pledge?.products?.[0]?.parameters?.fees);
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['DIVERSIFICATION_INVESTMENT', 'SOCIAL_SHARE'],
    },
    {
      name: 'fpciFees',
      template: 'fpciFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.fees;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FPCI'],
    },
    {
      name: 'fcprFees',
      template: 'fcprFees',
      title: 'Frais',
      callbackValidation: (pledge: PledgeRead) => {
        return !!pledge?.products?.[0]?.parameters?.fees;
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['FCPR', 'FPS'],
    },
    // immobile
    {
      name: 'realEstateCharacteristics',
      template: 'realEstateCharacteristics',
      title: 'Caractéristiques du bien Immobilier',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateCharacteristics2',
      template: 'realEstateCharacteristics2',
      title: 'Caractéristiques du bien Immobilier 2',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateCosts',
      template: 'realEstateCosts',
      title: 'Frais du bien Immobilier',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateOwnerShipType',
      template: 'realEstateOwnerShipType',
      title: 'Vous souhaitez effectuer cette souscription :',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateFiscalLaw',
      template: 'realEstateFiscalLaw',
      title: 'Dispositif fiscal choisi',
      type: 'products',
      callbackValidation: entity => {
        return this.cBService.getRealEstateFiscalLaw(entity);
      },
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateInvestmentAmount',
      template: 'realEstateInvestmentAmount',
      title: "Montant de l'investissement",
      type: 'products',
      callbackValidation: entity => {
        return this.cBService.getRealEstateInvestmentAmountForm(entity);
      },
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstatePinelParameters',
      template: 'realEstatePinelParameters',
      title: 'Paramètres Pinel',
      callbackBasedOn: (pledge: PledgeRead, user) => {
        const productType = pledge?.productType;

        return !['PINEL', 'PINEL_DOM_TOM'].includes(productType);
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },
    {
      name: 'realEstateLmplmnpParameters',
      template: 'realEstateLmplmnpParameters',
      title: 'Paramètres LMP/LMNP',
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
      callbackBasedOn: (pledge: PledgeRead, user) => {
        const { productType } = pledge || {};
        return !['BIC', 'MICRO_BIC', 'CENSI_BOUVARD'].includes(productType);
      },
    },
    {
      name: 'realEstateRiskAversion',
      template: 'realEstateRiskAversion',
      title: 'Aversion aux risques',
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!!user?.sessionConsultant) return true;
        const { realEstateInsvestmentAmount } =
          pledge?.products?.[0]?.parameters || {};
        const { patrimonyValueEstimation } = pledge?.parameters || {};

        return !(realEstateInsvestmentAmount > 0.15 * patrimonyValueEstimation);
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: ['UNFURNISHED', 'LMNP'],
    },

    //end of immobile
    {
      name: 'lifeInsuranceFees',
      template: 'lifeInsuranceFees',
      title: "Frais d'entrée de votre contrat.",
      category: 'Composition du contrat',
      callbackBasedOn: (entity: any, user: UserAuthentified) => {
        if (entity?.operation !== 'SUBSCRIPTION') return true;
        return !user?.sessionConsultant;
      },
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PER',
      ],
    },
    {
      name: 'peePercoFees',
      template: 'peePercoFees',
      title: 'Frais de votre contrat (PEE/PERCO).',
      category: 'Composition du contrat',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getPeePercoFeesForm(pledge);
      },
      callbackBasedOn: (entity: any, user: UserAuthentified) => {
        if (entity?.operation !== 'SUBSCRIPTION') return true;
        return !user?.sessionConsultant;
      },
      productType: ['PEE', 'PERCO'],
    },

    {
      id: 46,
      name: 'fundsInitialPayment',
      step: '46',
      showFirstTime: true,
      template: 'fundsInitialPayment',
      callbackValidation: entity => {
        return this.cBService.getFundsInitialPayment(entity);
      },
      callbackBasedOn: (entity: any) => {
        const { operation = 'SUBSCRIPTION' } = entity || {};

        if (
          [
            'ARBITRAGE_INDIVIDUAL',
            'RACHAT_TOTAL',
            'RACHAT_PARTIEL',
            'AVANCE',
            'RACHAT_PARTIEL_PROGRAMME',
            'SUSPENSION_INDEXATION',
            'TRANSFER',
            'ORDRE_DE_REMPLACEMENT',
          ].includes(operation)
        ) {
          return true;
        }

        return false;
      },
      alwaysShow(entity: any, user) {
        const { operation = 'SUBSCRIPTION' } = entity || {};

        if (
          [
            'ARBITRAGE_INDIVIDUAL',
            'RACHAT_TOTAL',
            'RACHAT_PARTIEL',
            'AVANCE',
            'RACHAT_PARTIEL_PROGRAMME',
            'SUSPENSION_INDEXATION',
            'TRANSFER',
            'ORDRE_DE_REMPLACEMENT',
          ].includes(operation)
        ) {
          return false;
        }

        return true;
      },
      getTitle: (entity: PledgeRead) => {
        const { operation = 'SUBSCRIPTION' } = entity || {};

        if (operation === 'SUBSCRIPTION') {
          return "D'où proviendront les fonds de votre versement initial ?";
        }
        if (operation === 'VERSEMENT_COMPLEMENTAIRE') {
          return "D'où proviendront les fonds de votre versement complémentaire ?";
        }
        if (operation === 'VERSEMENT_PROGRAMME') {
          return "D'où proviendront les fonds de votre versement programmé ?";
        }

        return "D'où proviendront les fonds de votre versement initial ?";
      },
      type: 'products',
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
      ],
    },

    {
      name: 'fundsInitialPaymentScpi',
      template: 'fundsInitialPaymentScpi',
      showFirstTime: true,
      callbackValidation: entity => {
        const { products = [] } = entity || {};
        return products?.every(
          ({ parameters }) => !!parameters?.fundsOriginList?.length,
        );
      },
      type: 'owners',
      title: 'D’où proviendront les fonds de votre versement initial ?',
      category: 'Composition du contrat',
      productType: ['SCPI'],
    },

    {
      name: 'fees',
      template: 'fees',
      title: 'Signature du contrat.',
      isConsultant: true,
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
        'PRESTATION',
      ],
    },

    {
      name: 'document',

      template: 'document',
      title: 'Téléchargez votre pièce d’identité.',
      subTitle:
        'Pour souscrire au contrat, nous avons besoin de garantir votre identité.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getDocument(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.IDENTITY_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
        'PRESTATION',
      ],
    },
    {
      name: 'justificatifDomicile',

      template: 'justificatifDomicile',
      title: 'Téléchargez votre justificatif de domicile.',
      subTitle:
        'Ce document permet de confirmer l’adresse que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        // return this.cBService.getJustificatifDomicile(pledge);
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.JUSTIFICATIF_DOMICILE_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
        'PRESTATION',
      ],
    },
    {
      name: 'justificatifOrigineDesFonds',
      template: 'justificatifOrigineDesFonds',
      title: 'Téléchargez vos justificatifs de provenance des fonds ',
      subTitle:
        'Ce document est obligatoire pour valider les fonds utilisés pour votre investissement.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        //return this.cBService.getJustificatifOrigineDesFonds(pledge);
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.JUSTIFICATIF_ORGINE_DES_FONDS_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
        'SCPI',
        'GIRARDIN',
        'CROWDFUNDING',
        'FCPI',
        'FPCI',
        'BOND',
        'GFV',
        'GFI',
        'CONTINGENCY',
        'INSURANCE',
        'GFF',
        'TITRES_PARTICIPATIFS',
        'SOCIAL_SHARE',
        'OBLIGATIONS',
        'DIVERSIFICATION_INVESTMENT',

        'FCPR',
        'FPS',
        'PRIVATE_EQUITY',
        'FIP',
        'FIP_DOMTOM',
        'FIP_CORSE',
        'LMNP',
        'UNFURNISHED',
        'CAT',
        'BORROWER_INSURANCE',
        'PRESTATION',
      ],
    },
    {
      name: 'cellphoneBillId',
      template: 'cellphoneBillId',
      title: 'Téléchargez votre facture de téléphone',
      subTitle:
        'Ce document permet de confirmer l’adresse que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        // return this.cBService.getPhoneBillId(pledge);
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.PHONE_BILL_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'PER',
      ],
    },
    {
      name: 'ribForm',
      template: 'ribForm',
      title: 'Téléchargez votre rib',
      subTitle:
        'Ce document permet de confirmer votre compte bancaire que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.RIB_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CROWDFUNDING',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'SCPI',
        'PER',
        'GIRARDIN',
      ],
    },
    {
      name: 'familyBookForm',
      template: 'familyBookForm',
      title: 'Téléchargez votre livret de famille',
      subTitle:
        'Ce document permet de confirmer votre situation familiale que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.LIVRET_DE_FAMILLE,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'SCPI',
        'PER',
      ],
    },
    {
      name: 'carteVitaleForm',
      template: 'carteVitaleForm',
      title: 'Téléchargez votre carte vitale',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.CARTE_VITALE_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: ['GIRARDIN'],
    },
    {
      name: 'justificatifSituationMatrimonialeForm',
      template: 'justificatifSituationMatrimonialeForm',
      title: 'Téléchargez votre justificatif de situation matrimoniale',
      subTitle:
        'Ce document permet de confirmer votre situation matrimoniale. (justificatif de divorce, de mariage, de PACS, de veuvage...)',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const ownerMaritalSituation = pledge?.owners?.[0]?.userMaritalSituation;
        if (
          !ownerMaritalSituation ||
          ['SINGLE', 'LIVETOGETHER'].includes(ownerMaritalSituation)
        ) {
          return true;
        }
        return !pledge?.parameters?.additionalQuestions?.find(
          q =>
            q.id ==
            CompanyFieldType.JUSTIFICATIF_SITUATION_MATRIMONIALE_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: ['GIRARDIN'],
    },
    {
      name: 'contratMariageForm',
      template: 'contratMariageForm',
      title: 'Téléchargez votre contrat de mariage',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        const ownerMaritalSituation = pledge?.owners?.[0]?.userMaritalSituation;
        const ownerUnionContract = !!pledge?.owners?.[0]?.unionContract;
        if (
          !ownerMaritalSituation ||
          !['MARRIED'].includes(ownerMaritalSituation) ||
          !ownerUnionContract
        ) {
          return true;
        }
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.CONTRAT_MARIAGE_DOCUMENT,
        )
          ? true
          : false;
      },
      category: 'Composition du contrat',
      productType: ['GIRARDIN'],
    },
    {
      name: 'offreDePretForm',
      template: 'offreDePretForm',
      title: 'Téléchargez votre offre de prêt',
      subTitle:
        'Ce document est nécessaire pour la complétion de votre dossier.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.OFFRE_DE_PRET_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'fsiForm',
      template: 'fsiForm',
      title: 'Téléchargez votre FSI',
      subTitle:
        'Ce document est nécessaire pour la complétion de votre dossier.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.FSI_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['BORROWER_INSURANCE'],
    },
    {
      name: 'kbisForm',
      template: 'kbisForm',
      title: 'Téléchargez votre kbis',
      subTitle:
        'Ce document permet de confirmer l’identité de votre personne morale que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.KBIS_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'SCPI',
        'CROWDFUNDING',
      ],
    },
    {
      name: 'attestationBeneficiairesEffectifsForm',
      template: 'attestationBeneficiairesEffectifsForm',
      title: 'Téléchargez votre attestation bénéficiaires effectifs',
      subTitle:
        'Ce document permet de confirmer les bénéficiaires effectifs de votre personne morale que vous avez précédemment indiquée.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q =>
            q.id ==
            CompanyFieldType.ATTESTATION_BENEFICIAIRES_EFFECTIFS_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: [
        'ASSURANCE_VIE',
        'CONTRAT_CAPI',
        'COMPTE_TITRE',
        'PEA',
        'PEI',
        'PEE',
        'PERCO',
        'SCPI',
        'CROWDFUNDING',
      ],
    },
    {
      name: 'accountResultsForm',
      template: 'accountResultsForm',
      title: 'Téléchargez votre compte de résultat',
      subTitle:
        'Ce document est obligatoire pour valider les fonds utilisés pour votre investissement.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.ACCOUNT_RESULTS_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'statutsSocieteForm',
      template: 'statutsSocieteForm',
      title: 'Téléchargez votre statuts certifiés conformes',
      subTitle:
        'Ce document est obligatoire pour valider les fonds utilisés pour votre investissement.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.STATUS_SOCIETE_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'rcsRegistrationExtractForm',
      template: 'rcsRegistrationExtractForm',
      title: "Téléchargez votre extrait d'immatriculation RCS",
      subTitle:
        'Ce document est obligatoire pour valider les fonds utilisés pour votre investissement.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.RCS_REGISTRATION_EXTRACT_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'bilanForm',
      template: 'bilanForm',
      title: 'Téléchargez votre bilan',
      subTitle:
        'Ce document est obligatoire pour valider les fonds utilisés pour votre investissement.',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return true;
      },
      callbackBasedOn: (pledge: PledgeRead) => {
        return !pledge?.parameters?.additionalQuestions?.find(
          q => q.id == CompanyFieldType.BILAN_DOCUMENT,
        )
          ? true
          : false;
      },

      category: 'Composition du contrat',
      productType: ['SCPI', 'CROWDFUNDING'],
    },
    {
      name: 'risquesClientPersonneMorale',
      template: 'risquesClientPersonneMorale',
      title: 'Risques client - Personne morale',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRisquesClientPersonneMorale(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        if (pledge?.parameters?.subscriberType === SubscriberTypeEnum.PHYSICAL)
          return true;
        return false;
      },
      category: 'LCB-FT',
      productType: [],
    },
    {
      name: 'risquesClientPersonnePhysique',
      template: 'risquesClientPersonnePhysique',
      title: 'Risques client - Personne physique',
      type: 'owners',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRisquesClientPersonnePhysique(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        if (pledge?.parameters?.subscriberType === SubscriberTypeEnum.COMPANY)
          return true;
        return false;
      },
      category: 'LCB-FT',
      productType: [],
    },
    {
      name: 'risquesOperations',
      template: 'risquesOperations',
      title: "Risques liés à l'opération",
      type: 'parameters',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRisquesOperations(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        return false;
      },
      category: 'LCB-FT',
      productType: [],
    },
    {
      name: 'risquesDistribution',
      template: 'risquesDistribution',
      title: 'Risques liés à la distribution',
      type: 'parameters',
      callbackValidation: (pledge: PledgeRead) => {
        return this.cBService.getRisquesDistribution(pledge);
      },
      callbackBasedOn: (pledge: PledgeRead, user) => {
        if (!user?.sessionConsultant) return true;
        return false;
      },
      category: 'LCB-FT',
      productType: [],
    },
  ];
}
